import type {
	AccountIdentifierType,
	ReportingLines,
	ReportingLinesUser,
} from '@atlaskit/teams-client/reporting-lines-client/utils/types';

import type { getReportingLinesFromKnowledgeDiscoveryQuery$data } from './__generated__/getReportingLinesFromKnowledgeDiscoveryQuery.graphql';

const ACCOUNT_IDENTIFIER_TYPES: readonly AccountIdentifierType[] = [
	'ATLASSIAN_ID',
	'BASE64_HASH',
	'UNKNOWN',
] as AccountIdentifierType[];

type KnowledgeDiscoveryField = NonNullable<
	getReportingLinesFromKnowledgeDiscoveryQuery$data['knowledgeDiscovery']
>;

type ReportingLinesField = Extract<
	NonNullable<KnowledgeDiscoveryField['reportingLines']>,
	{ readonly __typename: 'KnowledgeDiscoveryReportingLines' }
>;

type ReportingLinesUserField = NonNullable<ReportingLinesField['user']>;

export type KnowledgeDiscoveryReportingLinesPayload = ReportingLinesField;

type KnowledgeDiscoveryReportingLinesUser = ReportingLinesUserField;

const toIdentifierType = (identifierType?: string | null): AccountIdentifierType | undefined =>
	ACCOUNT_IDENTIFIER_TYPES.find((type) => type === identifierType);

const mapUser = (user: KnowledgeDiscoveryReportingLinesUser): ReportingLinesUser => ({
	accountIdentifier: user.accountIdentifier ?? undefined,
	identifierType: toIdentifierType(user.identifierType),
	numberOfDirectReports: user.numberOfDirectReports ?? 0,
	numberOfTotalReports: user.numberOfTotalReports ?? 0,
	pii: user.pii
		? {
				name: user.pii.name ?? undefined,
				picture: user.pii.picture ?? undefined,
				accountStatus: user.pii.accountStatus ?? undefined,
				extendedProfile: user.pii.extendedProfile
					? { jobTitle: user.pii.extendedProfile.jobTitle ?? undefined }
					: undefined,
			}
		: undefined,
});

/**
 * Maps a `KnowledgeDiscoveryReportingLines` payload into the `ReportingLines`
 * shape consumed by the reporting-lines store.
 *
 * Returns `undefined` when the payload has no root user.
 */
export const mapKnowledgeDiscoveryReportingLines = (
	payload: KnowledgeDiscoveryReportingLinesPayload | null | undefined,
): ReportingLines | undefined => {
	if (!payload?.user) {
		return undefined;
	}

	const mapSection = (
		users: ReadonlyArray<KnowledgeDiscoveryReportingLinesUser | null> | null | undefined,
	): ReportingLinesUser[] | undefined =>
		users
			? users
					.filter((user): user is KnowledgeDiscoveryReportingLinesUser => user != null)
					.map(mapUser)
			: undefined;

	return {
		user: mapUser(payload.user),
		managers: mapSection(payload.managers),
		peers: mapSection(payload.peers),
		reports: mapSection(payload.reports),
	};
};
